import type {ButtonHTMLAttributes, PropsWithChildren} from "react";
import {Button as UIButton} from "@headlessui/react";
import clsx from "clsx";
import {LoaderPlain} from "../LoaderPlain";

type ButtonProps = PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>> & {
  variant?: "primary" | "secondary" | "ghost" | "negative" | "disabled" | "cancel";
  size?: "small" | "medium" | "large";
  loading?: boolean;
  disabled?: boolean;
};

const VARIANT_CLASSES: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "bg-primary text-white",
  secondary: "bg-secondary text-blackout",
  ghost: "bg-background text-blackout",
  negative: "bg-secondary text-primary",
  disabled: "bg-secondary text-secondary-text",
  cancel: "bg-background text-primary",
};

const SIZE_CLASSES: Record<NonNullable<ButtonProps["size"]>, string> = {
  small: "px-3 py-1.5 text-sm",
  medium: "px-4 py-3 text-sm font-bold",
  large: "px-6 py-4 text-base font-bold",
};

export const Button = ({
  children,
  className,
  variant: _variant = "primary",
  size = "medium",
  loading,
  disabled,
  ...props
}: ButtonProps) => {
  const variant = disabled || loading ? "disabled" : _variant;

  return (
    <UIButton
      {...props}
      className={clsx(
        "relative rounded-full transition-opacity duration-500 active:opacity-50",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      disabled={disabled || loading}
      type="button"
    >
      {loading ? (
        <>
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <LoaderPlain />
          </div>
          {` `}
        </>
      ) : (
        children
      )}
    </UIButton>
  );
};
